import { MarkdownViewer } from '@/business-components/markdown-viewer/MarkdownViewer';
import { useRequestExampleValue } from '@/business-components/request-parameter/hooks/useRequestExampleValue';
import { CustomTag } from '@/components/custom-tag/CustomTag';
import { SelectRadio } from '@/components/select-radio/SelectRadio';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useOperationConfigModelStore } from '@/models/operation-config/operationConfig';
import { ApiDocLayout } from '@/shared/constant/layout/ApiDocLayout';
import { FormatJSONCodeBlock } from '@/ui-kit/components/CodeBlock/FormatJSONCodeBlock';
import type { Example } from '@tarslib/types/lib/httpApi';
import { HttpApiRequestBodyType } from '@tarslib/types/lib/httpApi';
import { FoxIconChevronDownDouble, FoxIconChevronUpDouble } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import styles from './MultiExamples.module.css';

export type MultiExamplesProps = {
  type:
    | HttpApiRequestBodyType.Json
    | HttpApiRequestBodyType.Xml
    | HttpApiRequestBodyType.MsgPack
    | HttpApiRequestBodyType.Raw;
  value: (Example & {
    key: string;
  })[];
  className?: string;
};

export function MultiExamples(props: MultiExamplesProps) {
  const { type, className, value } = props;

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const apiDocLayout = useDocsDataModelStore((x) => x.docsBaseConfig.apiDocLayout);

  const isExpandRequestExample = useOperationConfigModelStore((x) => x.isExpandRequestExample);
  const setIsExpandRequestExample = useOperationConfigModelStore(
    (x) => x.setIsExpandRequestExample,
  );
  const options = React.useMemo(() => {
    return value.map((x) => {
      return {
        label: x.name,
        value: x.key,
      };
    });
  }, [value]);

  const { selectedRequestExample, setSelectedRequestKey } = useRequestExampleValue(value);
  if (!selectedRequestExample) {
    return null;
  }
  return (
    <div className={clsx('', className)}>
      {apiDocLayout === ApiDocLayout.TwoColumn ? (
        <div
          className={clsx(
            'flex h-8 cursor-pointer items-center px-3 py-1 text-sm font-base text-fill-7',
            {
              ['rounded-b-xl bg-fill-2']: !isExpandRequestExample,
            },
          )}
          onClick={() => {
            setIsExpandRequestExample(!isExpandRequestExample);
          }}
        >
          <span className="mr-2">{clientI18n('examples')}</span>
          {isExpandRequestExample ? (
            <FoxIconChevronUpDouble className="mx-0" size={12} />
          ) : (
            <FoxIconChevronDownDouble className="mx-0" size={12} />
          )}
        </div>
      ) : (
        <div className="h-4" />
      )}

      {(isExpandRequestExample || apiDocLayout === ApiDocLayout.OneColumn) && (
        <>
          {!!value.length && (
            <SelectRadio
              value={selectedRequestExample.key}
              options={options}
              type="radio"
              className="mb-1 px-3"
              minShowItemCount={1}
              onChange={setSelectedRequestKey}
              prefixNode={
                apiDocLayout === ApiDocLayout.OneColumn ? (
                  <div className="ml-1 mr-2 flex-shrink-0 text-base font-500 text-color">
                    {options.length > 1 ? clientI18n('examples') : clientI18n('api.label.example')}
                  </div>
                ) : null
              }
            />
          )}
          <div className="p-4">
            {selectedRequestExample.description && (
              <MarkdownViewer
                className={styles.description}
                value={selectedRequestExample.description}
                enableHeaderPlugin={false}
                inDocView={false}
              />
            )}

            {(type === HttpApiRequestBodyType.Xml ||
              type === HttpApiRequestBodyType.Json ||
              type === HttpApiRequestBodyType.MsgPack) && (
              <>
                <FormatJSONCodeBlock
                  code={selectedRequestExample.value}
                  language={type === HttpApiRequestBodyType.Xml ? 'xml' : 'json'}
                  theme="light"
                  onlyCode={false}
                  className={styles['request-example']}
                />
              </>
            )}
            {type === HttpApiRequestBodyType.Raw && (
              <>
                <div>
                  <span className="mr-2 text-sm font-400 leading-6 text-secondary">
                    {clientI18n('api.parameterLabel.example')}:
                  </span>
                  <CustomTag>{selectedRequestExample.value}</CustomTag>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
